/**
 * String / format utilities — pure functions, no `this` access.
 *
 * Phase 2 modularization Sprint 3 (2026-05-07).
 * Извлечено из ProfessionalQuoteCalculator (public/js/app.js).
 *
 * Глобально экспортируется как window.StringUtils для legacy-кода;
 * также CommonJS export для тестов.
 */
(function (global) {
    'use strict';

    /**
     * HTML-escape для безопасного включения в innerHTML.
     * Использует браузерное API (textContent → innerHTML).
     * @param {string} text
     * @returns {string}
     */
    function escapeHtml(text) {
        if (text === null || text === undefined) return '';
        const div = document.createElement('div');
        div.textContent = String(text);
        return div.innerHTML;
    }

    /**
     * Транслитерация кириллицы в латиницу.
     * @param {string} text
     * @returns {string}
     */
    function transliterate(text) {
        if (!text) return '';
        const map = {
            'а': 'a', 'б': 'b', 'в': 'v', 'г': 'g', 'д': 'd', 'е': 'e', 'ё': 'yo',
            'ж': 'zh', 'з': 'z', 'и': 'i', 'й': 'y', 'к': 'k', 'л': 'l', 'м': 'm',
            'н': 'n', 'о': 'o', 'п': 'p', 'р': 'r', 'с': 's', 'т': 't', 'у': 'u',
            'ф': 'f', 'х': 'h', 'ц': 'ts', 'ч': 'ch', 'ш': 'sh', 'щ': 'sch',
            'ъ': '', 'ы': 'y', 'ь': '', 'э': 'e', 'ю': 'yu', 'я': 'ya',
            ' ': '_'
        };
        return String(text).toLowerCase().split('')
            .map(char => map[char] !== undefined ? map[char] : char)
            .join('');
    }

    /**
     * @param {string} email
     * @returns {boolean}
     */
    function isValidEmail(email) {
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(email || ''));
    }

    /**
     * Парсинг одной CSV-строки, корректно обрабатывает кавычки.
     * @param {string} line
     * @returns {string[]}
     */
    function parseCSVLine(line) {
        const result = [];
        let current = '';
        let inQuotes = false;
        for (let i = 0; i < line.length; i++) {
            const char = line[i];
            if (char === '"') {
                inQuotes = !inQuotes;
            } else if (char === ',' && !inQuotes) {
                result.push(current.trim());
                current = '';
            } else {
                current += char;
            }
        }
        result.push(current.trim());
        return result;
    }

    /**
     * Экранирование поля для CSV (оборачивает в "..." если внутри , " или newline).
     * @param {*} field
     * @returns {string}
     */
    function escapeCSVField(field) {
        if (typeof field !== 'string') field = String(field);
        if (field.includes(',') || field.includes('"') || field.includes('\n')) {
            return `"${field.replace(/"/g, '""')}"`;
        }
        return field;
    }

    /**
     * Локализованный formatDate для UI отображения (ru-RU dd.mm.yyyy).
     * Принимает ISO-string (YYYY-MM-DD) — без часовых поясов.
     * @param {string} dateString
     * @returns {string}
     */
    function formatDate(dateString) {
        if (!dateString) return '';
        const parts = String(dateString).split('-');
        if (parts.length !== 3) return dateString;
        const [year, month, day] = parts.map(p => parseInt(p, 10));
        if (!year || !month || !day) return dateString;
        const d = new Date(year, month - 1, day);
        if (isNaN(d.getTime())) return dateString;
        return d.toLocaleDateString('ru-RU', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    }

    /**
     * UUID v4 (RFC 4122).
     * Использует Math.random — не криптостойкий, но достаточный для UI-IDs.
     * Для security-токенов использовать crypto.randomUUID() (если доступно).
     * @returns {string}
     */
    function generateQuoteId() {
        // Если доступен crypto.randomUUID — используем (более криптостойкий)
        if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
            return crypto.randomUUID();
        }
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
            const r = Math.random() * 16 | 0;
            const v = c === 'x' ? r : (r & 0x3 | 0x8);
            return v.toString(16);
        });
    }

    /**
     * Сгенерировать имя файла из данных сметы.
     * @param {Object} quoteData - { id, paxCount, tourStart, clientName }
     * @returns {string}
     */
    function generateFilenameWithId(quoteData) {
        const id = quoteData.id || generateQuoteId();
        const pax = quoteData.paxCount || 0;
        const date = quoteData.tourStart || new Date().toISOString().split('T')[0];
        const clientName = (quoteData.clientName || '').trim();

        if (!clientName) {
            return `untitled_${date}_${pax}pax_${id}.json`;
        }
        const name = transliterate(clientName.toLowerCase()).replace(/\s+/g, '_');
        return `${name}_${date}_${pax}pax_${id}.json`;
    }

    const StringUtils = {
        escapeHtml,
        transliterate,
        isValidEmail,
        parseCSVLine,
        escapeCSVField,
        formatDate,
        generateQuoteId,
        generateFilenameWithId
    };

    if (typeof module !== 'undefined' && module.exports) {
        module.exports = StringUtils;
    }
    if (global) {
        global.StringUtils = StringUtils;
    }
})(typeof window !== 'undefined' ? window : globalThis);
